import { Box, EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME, styled } from '@chronosphereio/chrono-ui';
import { useHover } from '@chronosphereio/core';
import { GlobalFilter } from '@/plugin-system/runtime';
import { LabelFilterButtons } from './LabelFilterButtons';

// Creating these vars to show where the math comes from.
const filterButtonsWidth = 48;
const filterButtonsGap = 8;

/**
 * Recommended min width to use with table cells that are rendered using `TableCellWithLabelFilterButtonsProps` when
 * onAddFilters is undefined.
 */
export const TABLE_CELL_WITH_LABEL_MIN_WIDTH = 70;

/**
 * Recommended min width to use with table cells that are rendered using `TableCellWithLabelFilterButtonsProps` when
 * onAddFilters is set. This width accounts for the size of the buttons plus some adequate spacing to show content, so
 * the cell is not so small that the buttons get cut off or completely cover the content.
 */
export const TABLE_CELL_WITH_LABEL_FILTER_BUTTONS_MIN_WIDTH =
  TABLE_CELL_WITH_LABEL_MIN_WIDTH + filterButtonsWidth + filterButtonsGap;

export interface TableCellWithLabelFilterButtonsProps {
  /**
   * Name of the label. Will be used to generate relevant global filter values when clicking the
   * filter buttons.
   */
  labelName: string;
  /**
   * Value of the label. Will be used to generate relevant global filter values when clicking the
   * filter buttons. Will be the value rendered in the cell if `formattedLabelValue` is not set.
   */
  labelValue: string | undefined;
  /**
   * Value rendered in the cell. Can be useful if you want to modify the value just for rendering
   * (e.g. rendering `--` instead of an empty string when the label is empty). If not set, `value`
   * will be rendered in the cell.
   */
  formattedLabelValue?: string;
  /**
   * Callback of filters to be added. When this prop is provided, the cell will render add/remove buttons
   * for adding/removing global filters. When clicked, they will call this function with the relevant updates
   * to the filters.
   */
  onAddFilters?: (filters: GlobalFilter[]) => void;
}

const NON_BREAKING_SPACE = '\u00A0';

export function TableCellWithLabelFilterButtons({
  onAddFilters,
  labelName,
  labelValue,
  formattedLabelValue,
}: TableCellWithLabelFilterButtonsProps) {
  const cellWithLabelFilters = (hovered: boolean) => {
    // We fall back to a non-breaking space if the value is falsy, to ensure that it takes up a little space in the
    // div, so that the hover behavior works as expected.
    const renderedValue = (formattedLabelValue ?? labelValue) || NON_BREAKING_SPACE;

    return (
      <TableCellWithLabelFilterButtonsWrapper>
        <div className={EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME}>{renderedValue}</div>
        {hovered && !!onAddFilters && (
          <LabelFilterButtons
            onAddFilters={onAddFilters}
            labels={{
              [labelName]: labelValue ?? '',
            }}
          />
        )}
      </TableCellWithLabelFilterButtonsWrapper>
    );
  };
  const [hoverableCellWithLabelFilters] = useHover(cellWithLabelFilters);
  return hoverableCellWithLabelFilters;
}

const TableCellWithLabelFilterButtonsWrapper = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    height: '100%',
    flexGrow: 1,
  };
});
